/// <amd-module name="Wit/HtmlEditor/Plugins/TabToNextFocusableElementPlugin" />
import { Editor, EditorPlugin, PluginEvent, PluginEventType } from "roosterjs-react";

const pluginEventsWhichShouldResetFocus = new Set([PluginEventType.ContentChanged, PluginEventType.Input, PluginEventType.MouseDown]);

/**
 * By default, hyperlinks in the RoosterJs editor are not keyboard-accessible. See https://github.com/microsoft/roosterjs/issues/375
 * Furthermore, calling .focus() on an element that is inside the editor will not work.
 * As a way around this, this plugin will select the next focusable element when the user hits Tab,
 * and activate the selected hyperlink when the user hits Enter (if an element is selected).
 *
 * The focused element will reset when the user interacts with the editor, so "Enter" still works normally unless an element is selected.
 */
export class TabToNextFocusableElementPlugin implements EditorPlugin {
    private _editor: Editor | null;
    private _focusedElement: HTMLElement | null;

    public getName(): string {
        return "TabToNextFocusableElementPlugin";
    }

    public dispose(): void {
        this._removeNavigationShortcutHelp();
        this._editor = null;
    }

    public initialize(editor: Editor): void {
        this._editor = editor;
        this._focusedElement = null;
        this._addNavigationShortcutHelp();
    }

    public onPluginEvent(event: PluginEvent): void {
        if (event.eventType === PluginEventType.KeyDown) {
            this._handleKeyDown(event.rawEvent);
        } else if (pluginEventsWhichShouldResetFocus.has(event.eventType)) {
            this._focusedElement = null;
        }
    }

    private _handleKeyDown(event: KeyboardEvent): void {
        if (event.key === "Enter") {
            return this._activateFocusedElement(event);
        }

        if ((event.ctrlKey || event.metaKey) && event.key === "ArrowDown") {
            return this._selectNextFocusableElement(event);
        }

        if ((event.ctrlKey || event.metaKey) && event.key === "ArrowUp") {
            return this._selectPreviousFocusableElement(event);
        }

        this._focusedElement = null;
    }

    private _selectPreviousFocusableElement(event: KeyboardEvent) {
        return this._selectNextFocusableElement(event, true);
    }

    private _selectNextFocusableElement(event: KeyboardEvent, invert?: boolean) {
        const next = this._getNextFocusableElement(event, !!invert);
        if (!next) {
            this._focusedElement = null;
            this._removeLinkActivationShortcutHelp();
            return;
        }

        this._focusedElement = next;
        this._addLinkActivationShortcutHelp();
        // RoosterJs actively prevents elements inside the editor from getting focus, so
        // calling next.focus() does nothing. Instead, we can show focus by selecting the text.
        this._editor?.select(next);
        event.preventDefault();
        event.stopPropagation();
    }

    private _getNextFocusableElement(event: KeyboardEvent, invert: boolean): HTMLElement | null {
        if (!event.target || !(event.target instanceof HTMLElement)) {
            return null;
        }

        const content = event.target;
        // Of the focusable elements, only anchor tags can be inserted in the editor, and the tabIndex attribute cannot be added.
        const allFocusableElements = content.querySelectorAll("a");
        if (allFocusableElements.length === 0) {
            return null;
        }

        const focusedNode = this._focusedElement ?? this._editor?.getFocusedPosition()?.node;
        if (!focusedNode) {
            return invert ? null : allFocusableElements[0];
        }

        if (invert) {
            for (const focusableElement of Array.from(allFocusableElements).reverse()) {
                if (isBefore(focusableElement, focusedNode)) {
                    return focusableElement;
                }
            }
        } else {
            for (const focusableElement of Array.from(allFocusableElements)) {
                if (isBefore(focusedNode, focusableElement)) {
                    return focusableElement;
                }
            }
        }

        return null;
    }

    private _activateFocusedElement(event: KeyboardEvent) {
        const href = this._focusedElement?.getAttribute("href");
        if (!href) {
            return;
        }
        window.open(href, "_blank");
        event.preventDefault();
        event.stopPropagation();
    }

    // Note: the editor does not provide a pre-baked way to get a reference to the editor htm
    // element, so we can't (easily) check existing attributes. Therefore, this is probably the
    // best we can do. Nothing else in the html editor is using aria-keyshortcuts, so this should
    // be fine.
    
    private _addNavigationShortcutHelp() {
        this._editor?.setEditorDomAttribute('aria-keyshortcuts', 'Control+ArrowDown Control+ArrowUp')
    }

    private _removeNavigationShortcutHelp() {
        this._editor?.setEditorDomAttribute('aria-keyshortcuts', '')
    }

    private _addLinkActivationShortcutHelp() {
        this._editor?.setEditorDomAttribute('aria-keyshortcuts', 'Control+ArrowDown Control+ArrowUp Enter')
    }

    private _removeLinkActivationShortcutHelp() {
        this._editor?.setEditorDomAttribute('aria-keyshortcuts', 'Control+ArrowDown Control+ArrowUp')
    }
}

function isBefore(maybeFirst: Node, maybeSecond: Node): boolean {
    const relativePosition = maybeFirst.compareDocumentPosition(maybeSecond);
    // The return value is a bit flag
    return !!(relativePosition & Node.DOCUMENT_POSITION_FOLLOWING);
}
